import { Icon, Label, token } from "@may-i/mash-design-system";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { AnalysisConfig } from "~/entities/analysis-config/types";
import type { Camera } from "~/entities/camera/types";
import type { FloorPlan } from "~/entities/floor-plan/types";
import { useCurrentUser } from "~/features/auth/hooks/use-current-user";
import { useToast } from "~/shared/ui/toast";
import { CameraFilterPopover, type CameraFilterState, type EventTypeAvailability } from "./CameraFilterPopover";

const LINE_TEXT = "#31B400";
const LINE_BG = "rgba(99, 251, 42, 0.16)";
const ZONE_TEXT = "#0090A3";
const ZONE_BG = "rgba(0, 224, 253, 0.16)";

interface SidebarHeaderProps {
  floorPlans: FloorPlan[];
  // (F17) 도면 필터 — 카메라 필터 팝오버 안 '도면' 섹션(드롭다운)으로 통합
  selectedFloorPlanIds: Set<number>;
  onFloorPlanFilterChange: (ids: Set<number>) => void;
  /** (F17) 카메라가 있는 도면 id — 대상 없는 도면은 필터에서 disabled */
  floorPlanIdsWithCameras: Set<number>;
  analyzedCount: number;
  mappedCount: number;
  lineTotalCount?: number;
  zoneTotalCount?: number;
  lineNormalCount?: number;
  lineFloatingCount?: number;
  lineVisitCount?: number;
  zoneNormalCount?: number;
  zonePurchaseCount?: number;
  cameraFilter: CameraFilterState;
  cameraFilterAvailability: EventTypeAvailability;
  /** 필터 매칭 카메라 수 — 필터 트리거 카운트 배지 */
  filteredCount: number;
  onCameraFilterChange: (filter: CameraFilterState) => void;
  onValidate?: () => void;
  onGenerateKeys?: () => void;
  isGeneratingKeys?: boolean;
  onClearAllKeys?: () => void;
  /** 선택된 도면에 비울 Key 가 없으면 '전체 초기화' 비활성 */
  clearAllKeysDisabled?: boolean;
  onOpenAddAdjustGuide?: () => void;
  /** 사이드바 콘텐츠 탭: cameras | memos */
  activeTab?: "cameras" | "memos";
  onTabChange?: (tab: "cameras" | "memos") => void;
  localConfigs: AnalysisConfig[];
  cameras: Camera[];
  getDisplayName: (camera: Camera) => string;
}

export function SidebarHeader({
  floorPlans,
  selectedFloorPlanIds,
  onFloorPlanFilterChange,
  floorPlanIdsWithCameras,
  analyzedCount,
  mappedCount,
  lineTotalCount = 0,
  zoneTotalCount = 0,
  lineNormalCount = 0,
  lineFloatingCount = 0,
  lineVisitCount = 0,
  zoneNormalCount = 0,
  zonePurchaseCount = 0,
  cameraFilter,
  cameraFilterAvailability,
  filteredCount,
  onCameraFilterChange,
  onValidate,
  onGenerateKeys,
  isGeneratingKeys = false,
  onClearAllKeys,
  clearAllKeysDisabled = false,
  onOpenAddAdjustGuide,
  activeTab = "cameras",
  onTabChange,
  localConfigs,
  cameras,
  getDisplayName,
}: SidebarHeaderProps) {
  const { t } = useTranslation("analysis-draft");
  const { showToast } = useToast();
  const { data: currentUser } = useCurrentUser();
  const isStaff = currentUser?.isStaff ?? false;

  const handleCopyKeys = useCallback(async () => {
    const lines: string[] = [];
    for (const cfg of localConfigs) {
      const camera = cameras.find((c) => c.id === cfg.cameraId);
      if (!camera) continue;
      const displayName = getDisplayName(camera);
      for (const line of cfg.countingLines) {
        lines.push(`${displayName}\t${line.analysisConfig?.name ?? ""}\t${line.key}`);
      }
      for (const zone of cfg.intervalZones) {
        lines.push(`${displayName}\t${zone.analysisConfig?.name ?? ""}\t${zone.key}`);
      }
    }
    if (lines.length === 0) return;
    await navigator.clipboard.writeText(lines.join("\n"));
    showToast({ type: "success", title: t("toast.copyKeysSuccess.title"), content: t("toast.copyKeysSuccess.content") });
  }, [localConfigs, cameras, getDisplayName, showToast, t]);

  return (
    <Container>
      <TitleRow>
        <Title>{t("title")}</Title>
      </TitleRow>
      <InfoSection>
        <InfoBox>
          <TopRow>
            <StatsRow>
              <StatText>{t("stats.analysis", { count: analyzedCount })}</StatText>
              <Divider />
              <StatText>{t("stats.mapped", { count: mappedCount })}</StatText>
            </StatsRow>
          </TopRow>
          <SummaryRows>
            <SummaryRow>
              <StyledLabel $text={LINE_TEXT} $bg={LINE_BG} variant="soft" color="default" size="small">
                line: {lineTotalCount}
              </StyledLabel>
              <SubStatsRow>
                <SubStatText>{t("summary.lineDefault")}: {lineNormalCount}</SubStatText>
                <Divider />
                <SubStatText>{t("summary.lineTraffic")}: {lineFloatingCount}</SubStatText>
                <Divider />
                <SubStatText>{t("summary.lineVisit")}: {lineVisitCount}</SubStatText>
              </SubStatsRow>
            </SummaryRow>
            <SummaryRow>
              <StyledLabel $text={ZONE_TEXT} $bg={ZONE_BG} variant="soft" color="default" size="small">
                zone: {zoneTotalCount}
              </StyledLabel>
              <SubStatsRow>
                <SubStatText>{t("summary.zoneDefault")}: {zoneNormalCount}</SubStatText>
                <Divider />
                <SubStatText>{t("summary.zonePurchase")}: {zonePurchaseCount}</SubStatText>
              </SubStatsRow>
            </SummaryRow>
          </SummaryRows>
        </InfoBox>
        <ActionRow>
          <TabsGroup>
            <Tab
              type="button"
              $active={activeTab === "cameras"}
              onClick={() => onTabChange?.("cameras")}
            >
              {t("tabs.cameras")}
            </Tab>
            <Tab
              type="button"
              $active={activeTab === "memos"}
              onClick={() => onTabChange?.("memos")}
            >
              {t("tabs.memos")}
            </Tab>
          </TabsGroup>
          <ActionRowRight>
          <CameraFilterPopover
            filter={cameraFilter}
            availability={cameraFilterAvailability}
            floorPlans={floorPlans}
            selectedFloorPlanIds={selectedFloorPlanIds}
            floorPlanIdsWithCameras={floorPlanIdsWithCameras}
            filteredCount={filteredCount}
            onApply={(filter, floorPlanIds) => { onCameraFilterChange(filter); onFloorPlanFilterChange(floorPlanIds); }}
          />
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <MoreButton>
                <Icon icon="ic_more" width={16} />
              </MoreButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <MoreMenuContent sideOffset={4} align="end">
                {/* 2-1: 고객사는 key 추출 / 유효성 검사 미노출 — 추가/조정 가이드만 노출 (확인 용도) */}
                {/* Key 작업 그룹: 섹션 라벨로 묶고, 반복되는 'Key' 접두어는 라벨로 흡수 */}
                {isStaff && (
                  <>
                    <MoreMenuLabel>{t("toolbar.keyGroupLabel")}</MoreMenuLabel>
                    <MoreMenuItem
                      onSelect={() => onGenerateKeys?.()}
                      disabled={isGeneratingKeys}
                    >
                      {t("toolbar.generateKeys")}
                    </MoreMenuItem>
                    <MoreMenuItem
                      onSelect={() => onClearAllKeys?.()}
                      disabled={clearAllKeysDisabled}
                    >
                      {t("toolbar.clearAllKeys")}
                    </MoreMenuItem>
                    <MoreMenuItem
                      onSelect={handleCopyKeys}
                      disabled={localConfigs.flatMap((c) => [...c.countingLines, ...c.intervalZones]).length === 0}
                    >
                      {t("toolbar.copyKeys")}
                    </MoreMenuItem>
                    <MoreMenuSeparator />
                  </>
                )}
                {isStaff && (
                  <MoreMenuItem onSelect={() => onValidate?.()}>
                    {t("toolbar.validate")}
                  </MoreMenuItem>
                )}
                {onOpenAddAdjustGuide && (
                  <MoreMenuItem onSelect={() => onOpenAddAdjustGuide()}>
                    {t("toolbar.addAdjustGuide")}
                  </MoreMenuItem>
                )}
              </MoreMenuContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          </ActionRowRight>
        </ActionRow>
      </InfoSection>
    </Container>
  );
}

// --- Styles ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background: #fff;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 8px 24px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 12px 8px 24px;
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
  background: #fafbff;
  border-radius: 8px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const StatText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  white-space: nowrap;
`;

const StyledLabel = styled(Label)<{ $text: string; $bg: string }>`
  --label-color: ${({ $text }) => $text};
  --label-background-color: ${({ $bg }) => $bg};
`;

const SummaryRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SubStatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SubStatText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: #696f8c;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(143, 149, 178, 0.24);
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TabsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 6px 0;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? token("colors.text.primary") : "transparent")};
  background: transparent;
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  line-height: 1.4;
  color: ${({ $active }) => ($active ? token("colors.text.primary") : token("colors.gray.GR600"))};
  cursor: pointer;

  &:hover {
    color: ${token("colors.text.primary")};
  }
`;

const ActionRowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${token("colors.text.secondary")};
  border-radius: 6px;

  &:hover {
    background: ${token("colors.action.hover")};
    color: ${token("colors.text.primary")};
  }
`;

const MoreMenuContent = styled(DropdownMenu.Content)`
  min-width: 160px;
  background: #fff;
  border: 1px solid ${token("colors.gray.GR300")};
  border-radius: 8px;
  padding: 4px 0;
  box-shadow: ${token("shadows.dropdown")};
  z-index: 50;
`;

const MoreMenuLabel = styled(DropdownMenu.Label)`
  padding: 6px 12px 2px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.secondary")};
`;

const MoreMenuSeparator = styled(DropdownMenu.Separator)`
  height: 1px;
  margin: 4px 0;
  background: ${token("colors.gray.GR300")};
`;

const MoreMenuItem = styled(DropdownMenu.Item)`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  cursor: pointer;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  outline: none;

  &:hover,
  &[data-highlighted] {
    background: ${token("colors.transparent.GR600.16")};
  }

  &[data-disabled] {
    opacity: 0.4;
    cursor: default;
  }
`;
